/**
 * Smart tagging's status line and "run now" button (FRONT-1670) —
 * `GET`/`POST /api/okf/enrichment(/run)` (`src/enrichment/routes.ts`).
 *
 * ── Progress is read from the database, not from this status ──────────────
 * The backend enqueues a background pass and returns `{ queued, startedAt }`
 * immediately; the queue is in-process per replica, so nothing about a run's
 * progress can be asked of it. The card reads progress from
 * `GET /api/okf/enrichment/activity?since=<startedAt>` (the tried-at stamp each
 * finished pass leaves on its item, tagged or not) — see `use-enrichment-activity.ts` and
 * `use-smart-tagging-progress.ts`. This status read (whole-corpus counts, a
 * full body scan per call) is therefore never polled; it is refetched once
 * when a pass ends.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { stopRun, type RunCounts } from '~/features/settings/smart-tagging-progress';
import { getStoredRun, recordRunStart, saveStoredRun } from '~/features/settings/smart-tagging-run-store';
import { useCurrentUser } from './use-is-admin';

export interface KnowledgeEnrichmentStatus {
  /** The SAVED `smartTagging` toggle (`src/okf/settings.ts`) — not the
   *  unsaved draft the settings screen may be holding. */
  enabled: boolean;
  total: number;
  /** Items with no tags at all. */
  untagged: number;
  /** Untagged AND never enriched AND either with enough readable text or —
   *  when the saved "Also tag images" switch is on and a vision model exists —
   *  a readable picture: what a "run now" click can actually queue. */
  eligible: number;
  /** Whether a vision-capable model exists for this workspace. `null` when
   *  the saved "Also tag images" switch is off (the server did not ask). When
   *  `false`, images are not counted in `eligible` and are skipped. */
  imageModelAvailable: boolean | null;
}

export interface RunEnrichmentResponse {
  queued: number;
  /** The server's clock, taken BEFORE anything was enqueued — the `since`
   *  the progress poll then counts finished passes from. */
  startedAt: string;
}

export interface StopEnrichmentResponse {
  stoppedAt: string;
}

export const knowledgeEnrichmentKeys = {
  all: ['knowledge-enrichment'] as const,
  /** `GET /api/okf/enrichment` takes no request input at all — it is
   *  self-scoped to the caller's own org+user from the request context, the
   *  same discipline as `okfSettingsKeys.settings()` — so there is nothing
   *  to fold into the key beyond the fixed prefix. */
  status: () => [...knowledgeEnrichmentKeys.all, 'status'] as const,
} as const;

/**
 * Smart-tagging status. A pass's progress is NOT read from this query any
 * more: the card polls `GET /api/okf/enrichment/activity` (database stamps)
 * for the live "Tagging N of M" and refreshes this status once when the pass
 * ends — see `use-smart-tagging-progress.ts`. This read stays a plain
 * fetch-on-mount (the status counts walk every body, so it is never polled).
 */
export function useKnowledgeEnrichment() {
  return useQuery({
    queryKey: knowledgeEnrichmentKeys.status(),
    queryFn: () => api.get<KnowledgeEnrichmentStatus>('/api/okf/enrichment'),
    // FRONT-2550: the global 60s staleTime would show the pre-upload total when
    // the card is reopened within a minute; always refetch on mount.
    refetchOnMount: 'always',
  });
}

/** Enqueues a smart-tagging pass over whatever is eligible right now. Stores
 *  the run for the card's progress block HERE, in the hook's own `onSuccess`
 *  (not a per-call `mutate` callback): the hook's callbacks still fire after
 *  the card unmounts, so leaving Settings right after pressing keeps the run.
 *  Does NOT decide when tagging is done — `useSmartTaggingProgress` does. */
export function useRunEnrichment() {
  const qc = useQueryClient();
  const { userId } = useCurrentUser();
  return useMutation({
    mutationFn: () => api.post<RunEnrichmentResponse>('/api/okf/enrichment/run'),
    onSuccess: (data) => {
      if (userId) recordRunStart(userId, data);
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.status() });
    },
  });
}

/** How long after a successful stop the final activity read waits. The item
 *  EDDY is on when the stop lands finishes (2.5-5 s each), and only its result
 *  makes the "Stopped at N of M" count right — so the card stays on
 *  "Stopping…" for this long, then reads once and freezes. */
export const FINAL_READ_DELAY_MS = 6_000;

/** The one final activity read after a stop, then the stored run frozen as
 *  `stopped` with those counts. A failed read freezes on the last known
 *  counts; a run that already ended (or was replaced) is left alone. */
async function freezeStoppedRun(userId: string): Promise<void> {
  const before = getStoredRun(userId);
  if (!before || (before.phase !== 'running' && before.phase !== 'quiet')) return;
  await new Promise<void>((resolve) => setTimeout(resolve, FINAL_READ_DELAY_MS));
  let answer: RunCounts | null = null;
  try {
    const qs = new URLSearchParams({ limit: '1', since: before.startedAt });
    const res = await api.get<{ sinceCounts?: RunCounts }>(`/api/okf/enrichment/activity?${qs.toString()}`);
    answer = res.sinceCounts ?? null;
  } catch {
    /* the last known counts stand */
  }
  // Re-read: a poll may have landed (or a new run replaced this one) during the await.
  const now = getStoredRun(userId);
  if (!now || now.startedAt !== before.startedAt) return;
  saveStoredRun(userId, stopRun(now, answer, Date.now()));
}

/** The stop request, then — only once it SUCCEEDED — the freeze. A rejected
 *  request throws before the store is touched, so the run keeps running. */
export async function stopEnrichment(userId: string | undefined): Promise<StopEnrichmentResponse> {
  const res = await api.post<StopEnrichmentResponse>('/api/okf/enrichment/stop');
  if (userId) await freezeStoppedRun(userId);
  return res;
}

/** "Stop tagging" — `POST /api/okf/enrichment/stop`. The stop is recorded
 *  HERE, in the hook's own mutation function (like `useRunEnrichment`'s
 *  `onSuccess`), so it still lands if the card unmounts mid-request. An error
 *  leaves the stored run untouched — it keeps running. */
export function useStopEnrichment() {
  const qc = useQueryClient();
  const { userId } = useCurrentUser();
  return useMutation({
    mutationFn: () => stopEnrichment(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
    },
  });
}
